import { Search, X, type LucideIcon } from 'lucide-react';
import { forwardRef, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import styles from './Input.module.css';

// Ours, never a bare browser control: a stock input ignores our palette, corners,
// shadow and motion, so it always looks foreign (CLAUDE.md §5).

type NativeInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'size'>;

interface InputProps extends NativeInputProps {
  readonly isInvalid?: boolean;
  readonly icon?: LucideIcon;
  /** A currency symbol or unit shown inside the control (06-qol §7). */
  readonly prefix?: string;
  readonly suffix?: string;
  /** Tabular numerals and right alignment, so figures line up down a column. */
  readonly isNumeric?: boolean;
  /** Roboto Mono, for identifiers — an employee id, a requisition number. */
  readonly isMono?: boolean;
  readonly onClear?: () => void;
  /**
   * `quiet`: reads as plain text until hovered or focused — for lines edited where they are read, such as the
   * ideal candidate's (ADR 0076). The focus ring and an error border still show.
   */
  readonly variant?: 'box' | 'quiet';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { isInvalid = false, icon: Icon, prefix, suffix, isNumeric, isMono, onClear, disabled, variant = 'box', ...rest },
  ref,
): ReactNode {
  const wrapClasses = [
    styles.wrap,
    variant === 'quiet' ? styles.quiet : '',
    isInvalid ? styles.invalid : '',
    disabled === true ? styles.disabled : '',
  ]
    .filter(Boolean)
    .join(' ');

  const inputClasses = [styles.input, isNumeric === true ? styles.numeric : '', isMono === true ? styles.mono : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapClasses}>
      {Icon && (
        <span className={styles.adornment} aria-hidden>
          <Icon size={15} />
        </span>
      )}
      {prefix !== undefined && (
        <span className={`${styles.adornment} ${styles.prefix}`} aria-hidden>
          {prefix}
        </span>
      )}
      <input
        {...rest}
        ref={ref}
        className={inputClasses}
        disabled={disabled}
        aria-invalid={isInvalid || undefined}
      />
      {suffix !== undefined && (
        <span className={styles.adornment} aria-hidden>
          {suffix}
        </span>
      )}
      {onClear && rest.value !== undefined && rest.value !== '' && (
        <button type="button" className={styles.clear} onClick={onClear} aria-label="Clear">
          <X size={13} aria-hidden />
        </button>
      )}
    </div>
  );
});

interface SearchInputProps extends Omit<InputProps, 'icon'> {
  readonly placeholder?: string;
}

/** `/` focuses the primary search or filter input on any list screen (06-qol §2). */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { placeholder = 'Search', ...rest },
  ref,
): ReactNode {
  return <Input {...rest} ref={ref} icon={Search} placeholder={placeholder} type="search" />;
});

type NativeTextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'>;

interface TextareaProps extends NativeTextareaProps {
  readonly isInvalid?: boolean;
  readonly maxLength?: number;
  /** When the counter appears, if a screen's spec names it (SCR-01: at 4,500 of 5,000). */
  readonly counterFrom?: number;
  /** As Input's: plain text until hovered or focused, and as tall as what is typed (ADR 0076). */
  readonly variant?: 'box' | 'quiet';
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { isInvalid = false, maxLength, counterFrom, value, disabled, variant = 'box', ...rest },
  ref,
): ReactNode {
  const length = typeof value === 'string' ? value.length : 0;
  // The counter only appears once it starts to matter — a counter on an empty
  // field is a warning about a problem nobody has yet.
  const showCounter = maxLength !== undefined && (counterFrom !== undefined ? length >= counterFrom : length > maxLength * 0.8);

  const wrapClasses = [
    styles.wrap,
    styles.textareaWrap,
    variant === 'quiet' ? styles.quiet : '',
    isInvalid ? styles.invalid : '',
    disabled === true ? styles.disabled : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapClasses}>
      <textarea
        {...rest}
        ref={ref}
        value={value}
        disabled={disabled}
        maxLength={maxLength}
        className={`${styles.input} ${styles.textarea}`}
        aria-invalid={isInvalid || undefined}
      />
      {showCounter && (
        <span className={`${styles.counter} ${length >= maxLength ? styles.counterOver : ''}`}>
          {length} / {maxLength}
        </span>
      )}
    </div>
  );
});
